<script setup>
import { set, useFullscreen, useStorage } from '@vueuse/core'
import xTipsSimple from '@/components/common/xTipsSimple.vue'
import TradingView from './TradingView.vue'
import MarketDepth from './MarketDepth.vue'

import Indicators from '~/assets/images/spot/icon-indicators.svg'
import Camera from '~/assets/images/spot/icon-camera.svg'
import FullScreen from '~/assets/images/spot/icon-fullscreen.svg'
import IconZoomout from '~/assets/images/spot/ic-zoomout.svg'
import Setting from '~/assets/images/spot/icon-setting.svg'

const { t } = useI18n()
const resolution = useStorage('ttx-resolution-future', '15')
const resolutionPages = useStorage('ttx-resolution-pages-future', ['0', '15', '60', '240', '1D'])
const tab = ref('chart')
const tabs = computed(() => [
  { key: 'chart', label: t('走势图') },
  { key: 'marketDepth', label: t('深度图') },
])

const resolutionsAll = computed(() => [{
  key: '0',
  value: '0',
  label: t('分时'),
}, {
  key: '1',
  value: '1',
  label: '1M',
}, {
  key: '5',
  value: '5',
  label: '5M',
}, {
  key: '15',
  value: '15',
  label: '15M',
}, {
  key: '30',
  value: '30',
  label: '30M',
}, {
  key: '60',
  value: '60',
  label: '1H',
}, {
  key: '240',
  value: '240',
  label: '4H',
}, {
  key: '720',
  value: '720',
  label: '12H',
}, {
  key: '1D',
  value: '1D',
  label: '1D',
}, {
  key: '1W',
  value: '1W',
  label: '1W',
}])
const resolutions = computed(() => resolutionsAll.value.filter(i => resolutionPages.value.includes(i.key)))
const reverseResolutions = computed(() => resolutionsAll.value.filter(i => !resolutionPages.value.includes(i.key)))
const curResolutionInfo = computed(() => resolutionsAll.value.find(i => i.key === resolution.value))
const dom = ref()
const tv = ref()
const { toggle: toggleFullscreen, isFullscreen } = useFullscreen(dom)
const hasResolution = computed(() => resolutionPages.value.includes(resolution.value))

function setResolution(i) {
  set(resolution, i.key)
}
</script>

<template>
  <div ref="dom" class="flex flex-col flex-1 w-full h-full bg-fillPagePrimary">
    <ClientOnly>
      <div class="flex items-center flex-shrink-0 pe-3 space-x-4 text-xs border-b h-9 border-border whitespace-nowrap">
        <template v-if="tab === 'chart'">
          <div class="inline-flex items-center space-x-1.5 font-medium h-full">
            <button
              v-for="i in resolutions"
              :key="i.key"
              class="flex items-center h-6 px-3 font-medium border border-transparent hover:text-textPrimary text-textTertiary"
              :class="{ '!text-textPrimary bg-btnSecondary rounded-full !border-border': resolution === i.key }"
              @click="setResolution(i)"
            >
              {{ i.label }}
            </button>
            <n-popselect v-model:value="resolution" trigger="click" :options="reverseResolutions" placement="bottom-end" :show-checkmark="false">
              <div class="inline-flex items-center justify-center h-6 px-3 border rounded-full cursor-pointer bg-btnSecondary border-border text-textPrimary">
                <span class="me-0.5">
                  <template v-if="!hasResolution">
                    {{ curResolutionInfo.label }}
                  </template>
                  <template v-else>
                    {{ $t('更多') }}
                  </template>
                </span>
                <svgo-common-select-arrow class="w-1.5 h-1.5 text-textPrimary" />
              </div>
            </n-popselect>
          </div>
          <div class="w-px h-5 bg-border" />
          <div class="inline-flex items-center flex-1 space-x-3 text-textSecondary">
            <xTipsSimple :text="$t('指标')">
              <div
                class="cursor-pointer"
                aria-label="Indicator"
                @click="tv?.toggleIndicator"
              >
                <Indicators class="h-[18px] w-[18px] text-textSecondary hover:text-textPrimary" />
              </div>
            </xTipsSimple>
            <xTipsSimple :text="$t('快照')">
              <div
                class="cursor-pointer"
                aria-label="screenshot"
                @click="tv?.screenShot"
              >
                <Camera class="h-[14px] w-[14px] text-textSecondary hover:text-textPrimary" />
              </div>
            </xTipsSimple>
            <xTipsSimple :text="$t('设置')">
              <div
                class="cursor-pointer"
                aria-label="Setting"
                @click="tv?.toggleSetting"
              >
                <Setting class="w-[18px] h-[18px] text-textSecondary hover:text-textPrimary" />
              </div>
            </xTipsSimple>
          </div>
        </template>

        <div class="flex items-center gap-3 ms-auto">
          <common-x-tabs v-model="tab" :tabs="tabs" type="primary" class="h-7" />
          <xTipsSimple :text="isFullscreen ? t('退出全屏') : t('全屏')">
            <div
              class="inline-flex items-center justify-center rounded-full cursor-pointer w-7 h-7 bg-pageTertiary"
              aria-label="Fullscreen"
              @click="toggleFullscreen"
            >
              <IconZoomout v-if="isFullscreen" class="w-4 h-4 text-textSecondary hover:text-textPrimary" />
              <FullScreen v-else class="w-4 h-4 text-textSecondary hover:text-textPrimary" />
            </div>
          </xTipsSimple>
        </div>
      </div>
      <div class="relative flex-1 w-full overflow-hidden">
        <TradingView v-if="tab === 'chart'" ref="tv" :resolution="resolution" />
        <MarketDepth v-else />
      </div>
    </ClientOnly>
  </div>
</template>
